import { useMemo } from 'react';

export type NestedArrayError = Record<string, Record<string, string> | undefined>;
type ArrayItem = { id: string };

/**
 * A helper function which converts yup validation errors for a nested array of objects into a more suitable format
 * where errors for nested array objects can be referred by their id instead of an index.
 *
 * Mostly done to validate Uploader with file types dropdowns because it's not easy to move it's validation
 * to a library like react-hook-form without probably a major refactoring of the whole component
 *
 * Input:
 *  fieldName: "array"
 *  errors: { "array[1].foo": "Error", "array[2].bar": "Error" }
 *  values: [{ id: "aaa" }, { id: "bbb" }, { id: "ccc" }]
 *
 * Output: { "bbb": { foo: "Error" }, "ccc": { bar: "Error" } }
 *
 */

export function useNestedArrayError(fieldName: string, errors: Record<string, string>, values: ArrayItem[] = []) {
  // it's important to memoize against `errors` because `values` can change later (e.g. some middle item is removed)
  // and indices in `errors` will not match the indices in `values` anymore
  return useMemo(() => toNestedArrayError(fieldName, errors, values), [errors]);
}

function toNestedArrayError(fieldName: string, errors: Record<string, string>, values: ArrayItem[]): NestedArrayError {
  return Object.keys(errors).reduce<NestedArrayError>((result, errorPath) => {
    const match = errorPath.match(`${fieldName}\\[([0-9]+)\\]\\.(.+)`);

    if (!match) {
      return result;
    }

    const index = +match[1];
    const prop = match[2];
    const fieldId = values[index].id;

    if (!result[fieldId]) {
      result[fieldId] = {};
    }

    result[fieldId][prop] = errors[errorPath];

    return result;
  }, {});
}
